import { useEffect, useState } from "react";
import { Zap, X, TrendingUp, TrendingDown, LineChart } from "lucide-react";
import { publicSend } from "@/lib/derivPublic";
import { riseFallSignal } from "@/components/arcade/signalEngine";

const MARKETS = [
  ["R_10", "Vol 10", "synth"], ["R_25", "Vol 25", "synth"], ["R_50", "Vol 50", "synth"],
  ["R_75", "Vol 75", "synth"], ["R_100", "Vol 100", "synth"],
  ["1HZ10V", "Vol 10 (1s)", "synth"], ["1HZ25V", "Vol 25 (1s)", "synth"], ["1HZ50V", "Vol 50 (1s)", "synth"],
  ["1HZ75V", "Vol 75 (1s)", "synth"], ["1HZ100V", "Vol 100 (1s)", "synth"],
  ["frxEURUSD", "EUR/USD", "fx"], ["frxGBPUSD", "GBP/USD", "fx"], ["frxUSDJPY", "USD/JPY", "fx"],
  ["frxAUDUSD", "AUD/USD", "fx"], ["frxUSDCAD", "USD/CAD", "fx"],
  ["frxXAUUSD", "Gold", "fx"], ["frxXAGUSD", "Silver", "fx"],
];

/* ⚡ Pro Trade Signals feed — one place where every Rise/Fall setup appears.
   Runs the VTS Signal Engine (MA cross + trend gate + momentum) on 17 markets
   (10 Volatility + forex + gold) every 12s. Only WITH-trend setups pass; mixed
   or against-trend markets are greyed. Forex rows show "closed" off-session. */
const ProSignalsFeed = ({ currentSymbol, onLoadSymbol, onApplySignal, placeTrade, canTrade, placing, fullscreen }) => {
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState([]);
  const [scannedAt, setScannedAt] = useState(null);

  useEffect(() => {
    let stop = false;
    const scan = async () => {
      const out = await Promise.all(MARKETS.map(async ([sym, label, group]) => {
        try {
          const h = await publicSend({ ticks_history: sym, count: 50, end: "latest", style: "ticks" });
          const prices = (h?.history?.prices || []).map(Number);
          const times = h?.history?.times || [];
          const lastT = Number(times[times.length - 1] || 0);
          if (lastT > 0 && Date.now() / 1000 - lastT > 180) return { sym, label, group, closed: true };
          if (prices.length < 23) return null;
          return { sym, label, group, sig: riseFallSignal(prices) };
        } catch { return null; }
      }));
      if (stop) return;
      const score = (r) => (r.closed ? -999 : r.sig.strength - (r.sig.wait ? 100 : 0));
      setRows(out.filter(Boolean).sort((a, b) => score(b) - score(a)));
      setScannedAt(new Date());
    };
    scan();
    const t = setInterval(scan, 12000);
    return () => { stop = true; clearInterval(t); };
  }, []);

  const active = rows.filter((r) => !r.closed && r.sig && !r.sig.wait);

  const renderRow = (r) => {
    if (r.closed) {
      return (
        <div key={r.sym} data-testid={`pro-signal-row-${r.sym}`} className="flex items-center gap-2 rounded-lg border border-white/5 bg-white/[0.02] px-2.5 py-2 opacity-45">
          <span className="text-[11px] font-mono text-vts-silver/70 w-24 shrink-0">{r.label}</span>
          <span className="text-[10px] font-mono text-vts-silver/40">🌙 market closed — back with the next forex session</span>
        </div>
      );
    }
    const isCur = r.sym === currentSymbol;
    const s = r.sig;
    if (s.wait) {
      return (
        <div key={r.sym} data-testid={`pro-signal-row-${r.sym}`} className="flex items-center gap-2 rounded-lg border border-white/5 bg-white/[0.02] px-2.5 py-2 opacity-55">
          <span className="text-[11px] font-mono text-vts-silver/70 w-24 shrink-0">{r.label}</span>
          <span className="text-[10px] font-mono text-vts-silver/40">
            {(s.reasons || []).some((x) => x.includes("against the trend")) ? "⛔ against trend — gated" : "mixed — no setup"} · ↑{s.risePct ?? 50}% / ↓{100 - (s.risePct ?? 50)}%
          </span>
        </div>
      );
    }
    const rise = s.side === "RISE";
    return (
      <div key={r.sym} data-testid={`pro-signal-row-${r.sym}`} className={`rounded-lg border p-2.5 space-y-1.5 ${rise ? "border-vts-profit/40 bg-vts-profit/5" : "border-vts-loss/40 bg-vts-loss/5"}`}>
        <div className="flex items-center gap-2 flex-wrap">
          <span className={`inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-[11px] font-black text-white ${rise ? "bg-vts-profit" : "bg-vts-loss"}`}>
            {rise ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}{s.side}
          </span>
          <span className="text-[12px] font-bold text-white">{r.label}</span>
          <span data-testid={`pro-signal-dur-${r.sym}`} title={s.durationWhy}
            className="inline-flex items-center gap-1 rounded-md border border-vts-blue/40 bg-vts-blue/10 px-1.5 py-0.5 text-[10px] font-mono font-bold text-vts-blue">
            ⏱ {s.durationLabel}
          </span>
          {s.trend !== 0 && (
            <span className={`rounded-md px-1.5 py-0.5 text-[9px] font-mono font-bold ${s.trend > 0 ? "bg-vts-profit/15 text-vts-profit" : "bg-vts-loss/15 text-vts-loss"}`}>
              🧭 trend {s.trend > 0 ? "UP" : "DOWN"}
            </span>
          )}
          <span className="text-[10px] font-mono text-amber-400 font-black ml-auto">⚡ {s.strength}/100</span>
        </div>
        <div className="h-1.5 rounded-full bg-white/10 overflow-hidden">
          <div className={`h-full rounded-full ${rise ? "bg-vts-profit" : "bg-vts-loss"}`} style={{ width: `${s.strength}%` }} />
        </div>
        <p className="text-[9.5px] font-mono text-vts-silver/70 leading-snug">{(s.reasons || []).join(" · ")} · {s.durationWhy}</p>
        <div className="flex items-center gap-2 pt-0.5">
          {isCur ? (
            <button data-testid={`pro-signal-trade-${r.sym}`} disabled={!canTrade || placing}
              onClick={() => placeTrade(rise ? "CALL" : "PUT")}
              className={`flex items-center gap-1.5 rounded-lg px-3 min-h-[36px] text-[11px] font-bold text-white transition disabled:opacity-40 ${rise ? "bg-vts-profit hover:brightness-110" : "bg-vts-loss hover:brightness-110"}`}>
              {rise ? <TrendingUp className="h-3.5 w-3.5" /> : <TrendingDown className="h-3.5 w-3.5" />}
              Trade {s.side} now
            </button>
          ) : (
            <button data-testid={`pro-signal-load-${r.sym}`}
              onClick={() => { (onApplySignal || onLoadSymbol)(onApplySignal ? r : r.sym); setOpen(false); }}
              className="flex items-center gap-1.5 rounded-lg border border-vts-blue/50 bg-vts-blue/10 px-3 min-h-[36px] text-[11px] font-bold text-vts-blue hover:bg-vts-blue/20 transition">
              <LineChart className="h-3.5 w-3.5" /> Open chart + mark the plan
            </button>
          )}
          {isCur && (
            <button data-testid={`pro-signal-remark-${r.sym}`} onClick={() => { onApplySignal?.(r); setOpen(false); }}
              className="text-[10px] font-mono text-vts-blue underline underline-offset-2">re-mark chart</button>
          )}
        </div>
      </div>
    );
  };

  return (
    <>
      {!fullscreen && (
        <button data-testid="pro-signals-button" onClick={() => setOpen(true)} title="Live Rise/Fall signals — all markets in one feed"
          className="flex items-center gap-1.5 rounded-lg border border-white/10 bg-[#1E2638] min-h-[44px] px-3 text-white hover:border-vts-blue/60 hover:bg-[#252F45] transition-colors">
          <Zap className={`h-3.5 w-3.5 ${active.length ? "text-amber-400" : "text-vts-silver/50"}`} />
          <span className="hidden xs:inline">Signals</span>
          {active.length > 0 && (
            <span data-testid="pro-signals-count" className="rounded-full bg-amber-400 text-black text-[10px] font-black px-1.5 leading-4">{active.length}</span>
          )}
        </button>
      )}

      {open && (
        <div className="fixed inset-0 z-[75]">
          <div data-testid="pro-signals-backdrop" className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setOpen(false)} />
          <div data-testid="pro-signals-sheet" className="absolute inset-x-0 bottom-0 flex max-h-[88vh] flex-col rounded-t-2xl border-t border-vts-line bg-vts-bg shadow-2xl lg:inset-auto lg:top-1/2 lg:left-1/2 lg:-translate-x-1/2 lg:-translate-y-1/2 lg:w-[780px] lg:max-w-[92vw] lg:max-h-[84vh] lg:rounded-2xl lg:border">
            <div className="flex items-center gap-2 border-b border-vts-line p-3 shrink-0">
              <Zap className="h-5 w-5 text-amber-400" />
              <div className="min-w-0">
                <p className="text-[14px] font-bold text-white leading-tight">Rise/Fall Signals</p>
                <p className="text-[9px] font-mono text-vts-silver/50">17 markets · Volatility + Forex + Gold · MA cross & trend-gated · re-scans every 12s{scannedAt ? ` · last ${scannedAt.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" })}` : ""}</p>
              </div>
              <button data-testid="pro-signals-close" onClick={() => setOpen(false)} className="ml-auto p-1.5 rounded-lg hover:bg-vts-surface"><X className="h-5 w-5 text-vts-silver/60" /></button>
            </div>

            <div className="flex-1 overflow-y-auto p-3 space-y-2">
              {rows.length === 0 && <p className="text-[11px] font-mono text-vts-silver/50 animate-pulse py-6 text-center">⚡ scanning 17 markets…</p>}

              {active.length === 0 && rows.length > 0 && (
                <div data-testid="pro-signals-none" className="rounded-lg border border-dashed border-amber-400/40 bg-amber-400/5 p-3">
                  <p className="text-[12px] font-bold font-mono text-amber-400">⏳ NO CLEAR SETUP right now</p>
                  <p className="text-[10px] font-mono text-vts-silver/60 mt-0.5">nothing is trending cleanly — waiting IS the strategy. New scan every 12s.</p>
                </div>
              )}

              {rows.some((r) => r.group === "synth") && (
                <p className="text-[9px] font-black font-mono uppercase tracking-[0.2em] text-vts-silver/40 pt-1">Volatility · 24/7</p>
              )}
              <div className="space-y-2 lg:grid lg:grid-cols-2 lg:gap-2 lg:space-y-0">
                {rows.filter((r) => r.group === "synth").map(renderRow)}
              </div>

              {rows.some((r) => r.group === "fx") && (
                <p className="text-[9px] font-black font-mono uppercase tracking-[0.2em] text-vts-silver/40 pt-2">Forex · Gold · Silver</p>
              )}
              <div className="space-y-2 lg:grid lg:grid-cols-2 lg:gap-2 lg:space-y-0">
                {rows.filter((r) => r.group === "fx").map(renderRow)}
              </div>

              <p className="text-[9px] font-mono text-vts-silver/40 pt-1 text-center">
                only WITH-trend setups pass the gate (MA cross · price vs 20-tick avg · momentum · streaks) — conditions lining up, NEVER a guarantee. Trade with a budget.
              </p>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ProSignalsFeed;
